import React, { FC } from 'react';

import { api } from '@api/reduxApi';
import { Checkbox, Text } from '@components/common';
import { useToaster } from '@components/stores/toaster';
import { useDisabledFeatures } from '@hooks/useDisabledFeatures';

import { DisabledFeatureToggle, DisabledFeatureToggleProvider } from './DisabledFeatureToggle';

export const BetterDisabledFeatures: FC = () => {
  const { disabledFeatures, setDisabledFeatures } = useDisabledFeatures();
  const [updateFeatures] = api.useUpdateAccountDisabledFeaturesWithPartialObjectMutation();

  const showToast = useToaster();

  const handleOnToggle = async (feature: ToggleableFeature, childFeatures: ToggleableFeature[] = []) => {
    const newFeatures: Partial<Record<ToggleableFeature, boolean>> = {};

    newFeatures[feature] = !disabledFeatures[feature];

    // When disabling a parent feature, also disable all its children
    if (newFeatures[feature]) {
      childFeatures.forEach((childFeature) => {
        newFeatures[childFeature] = true;
      });
    }

    try {
      setDisabledFeatures(newFeatures);
      await updateFeatures({ disabled_features: newFeatures }).unwrap();
    } catch {
      showToast({
        heading: 'Failed to update',
        icon: 'error',
        text: 'Something went wrong updating disabled features. Please try again later.'
      });
    }
  };

  const contextValue = {
    disabledFeatures,
    handleOnToggle
  };

  return (
    <section>
      <Text h='600' bold>
        Feature access
      </Text>
      <Text h='400' color='gray-500' mb='4'>
        Decide which features users on your account have access to. When toggled off, users won&apos;t see these
        capabilities in Great Question.
      </Text>

      <DisabledFeatureToggleProvider value={contextValue}>
        <div className='space-y-2'>
          <Text h='500' bold mb='3'>
            Candidates
          </Text>
          <DisabledFeatureToggle
            name='Add candidates'
            featureKey='adding_candidates'
            description='The ability to add new candidates to the CRM in the UI manually, via link, or via CSV. Adding candidates via API or external CRM integration is not affected.'
          >
            <DisabledFeatureToggle
              name='Import candidates via CSV'
              featureKey='adding_candidates_via_csv'
              description='The ability to import candidates via CSV into the candidate pool or directly into studies.'
            />
          </DisabledFeatureToggle>
          <DisabledFeatureToggle
            name='Export candidates as CSV'
            featureKey='exporting_candidates_via_csv'
            description='The ability to download candidates from studies or the candidate table as CSV.'
          />
        </div>
        <div className='mt-6 space-y-2'>
          <Text h='500' bold mb='3'>
            Study types
          </Text>
          <DisabledFeatureToggle
            name='Create Online task'
            featureKey='online_task'
            description='The ability to create and run\nonline task studies through Great Question.'
          />
          <DisabledFeatureToggle
            name='Create Panel'
            featureKey='panel'
            description='The ability to create and run panel studies\nthrough Great Question.'
          />
          <DisabledFeatureToggle
            name='Create Survey'
            featureKey='survey'
            description='The ability to create and run survey studies\nthrough Great Question.'
          />
          <DisabledFeatureToggle
            name='Create Unmoderated test'
            featureKey='unmoderated_test'
            description='The ability to create and run unmoderated testing\nstudies through Great Question.'
          />
        </div>
        <div className='mt-6 space-y-2'>
          <Text h='500' bold mb='3'>
            Study settings
          </Text>
          <DisabledFeatureToggle
            name='Enable Livestreams'
            featureKey='livestream'
            description='The ability to have observers watch a live stream video\nof interviews conducted through Great Question.'
          />
          <DisabledFeatureToggle
            name='Set Incentives'
            featureKey='incentives'
            description='The ability to fund and pay incentives\nthrough our integration with Tremendous.'
          >
            <DisabledFeatureToggle
              name='Set External Recruitment'
              featureKey='external_recruitment'
              description='The ability to submit and pay for external recruitment requests\nthrough our integration with Respondent.'
            />
          </DisabledFeatureToggle>
          <DisabledFeatureToggle
            name='Export study responses'
            featureKey='exporting_study_responses'
            description='The ability to export screener responses and results from surveys, unmoderated, and panel studies.'
          />
        </div>
        <div className='mt-6 space-y-2'>
          <Text h='500' bold mb='3'>
            Repository
          </Text>
          <DisabledFeatureToggle
            name='Access to Repositories'
            featureKey='repository'
            description='The ability to access all Repositories and prevent users to access and view recordings.'
          >
            <DisabledFeatureToggle
              name='Access to Workspace repository'
              featureKey='repository_workspace'
              type='checkbox'
              description='The ability to access the Workspace repository.'
            />
            <DisabledFeatureToggle
              name='Export study recordings'
              featureKey='repository_export_study_recordings'
              description='The ability to download study recordings.'
            />
          </DisabledFeatureToggle>
        </div>
      </DisabledFeatureToggleProvider>
    </section>
  );
};
